/**
 * round time down to full minutes, e.g. 11:25:12 => 11:25
 * @param time time in epoch milliseconds
 * @returns rounded time in epoch milliseconds
 */
import {DeliveryAddressJSON, Seconds} from "./backend.service";
import {Host} from "./models/host";

/**
 * format a time in t4d format (e.g. seconds since midnight)
 * into a readable string (hh:mm)
 *
 * @param time  seconds since midnight
 */
export function format_time(time: number): string {
  return ("00" + Math.floor(time/3600)).slice(-2) + ':' +
         ("00" + Math.floor(time%3600/60)).slice(-2)
}

export function round_to_minutes(time: number): number {
  const minutes = Math.floor(time / ( 60 * 1000));
  return minutes * 60 * 1000;
}

/**
 * round time to the next quarter hour
 * e.g. 11:09 => 11:15, 10:22 => 10:30, ...
 *
 * @param time time in epoch milliseconds
 * @return rounded time in epoch milliseconds
 */
export function round_to_quarterhours(time: number): number {
  const minutes = Math.floor(time / ( 60 * 1000)),
    minute = minutes % 60;

  let result = minutes - minute;  // full hour

  if (minute > 45) {
    result += 60;
  } else if (minute > 30) {
    result += 45;
  } else if (minute > 15) {
    result += 30;
  } else if (minute > 0) {
    result += 15;
  }
  return result * 60 * 1000;
}

// pad with leading zeroes. Only needed on IE11.
function pad(num, size) {
    num = num.toString();
    while (num.length < size) num = "0" + num;
    return num;
}

/**
 * Get a relative time, e.g. "today at 13:00", "tomorrow at 11:00", "monday at 12:00".
 *
 * We do this by calculating epoch days (e.g. divide epoch milliseconds through 24*60*60*1000),
 * after substracting the current timezone offset, then comparing the days.
 *
 * @param time
 * @param host
 * @param approximate use approximate wording (e.g. today about 11:30)
 * @return label
 */
export function relative_time(time: Date, host: Host, approximate=false): string {
  const now = getCurrentDate(),
        today = Math.floor((now.getTime() - now.getTimezoneOffset()*60*1000) / (24*60*60*1000)),
        destday = Math.floor((time.getTime() - time.getTimezoneOffset()*60*1000) / (24*60*60*1000));
    let ftime = "";
    try {
      ftime = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: host.timezone });
    } catch(error) {
      // IE11 doesn't support converting to another timezone by name. Create a fake time going through
      // UTC and use that.
      const host_time = new Date();
      host_time.setTime(time.getTime() - time.getTimezoneOffset()*60*1000 + host.utc_offset*60*1000);
      ftime = pad(host_time.getHours(), 2) + ':' + pad(host_time.getMinutes(), 2);
    }

  if (today == destday) {
    return host.i18n[(approximate)?'today around':'today at'].replace('{time}', ftime);
  } else if ((destday - today) < 2) {
    return host.i18n[(approximate)?'tomorrow around':'tomorrow at'].replace('{time}', ftime);
  } else {
    return host.i18n[(approximate)?'around':'at'].replace('{time}', ftime)
                          .replace('{day}', time.toLocaleDateString([],{weekday: 'long'}))
  }
}

export function formatAddress(host: Host, address: DeliveryAddressJSON) {
  let result = "";
  if (address) {
    if (address.address) {
      result += address.address;
    }
    if (address.city) {
      let city = host.getCity(address.city);
      if (city) {
        if (city.zip) {
          if (result.length > 0) {
            result += ', ';
          }
          result += city.zip + " ";
        }
        if (city.city) {
          result += city.city;
        }
      }
    }
    return result;
  }
}

/**
 * return the time portion of dt as seconds since midnight
 * @param dt
 */
export function secondsSinceMidnight(dt: Date) {
  const midnight = new Date(dt);
  midnight.setHours(0,0,0,0);
  return (dt.getTime()-midnight.getTime())/1000;
}

/**
 * calculate seconts between to and from
 * @param to    to date
 * @param from  from date (optional, use now if not given)
 */
export function timeDelta(to: Date, from?: Date): Seconds {
  const t0 = from || getCurrentDate(),
        delta = to.valueOf() - t0.valueOf();
  return delta/1000;
}

/**
 * Get the current date. All code *MUST* use this function
 * instead of new Date() (see issue #297 for explanation).
 */
export function getCurrentDate(): Date {
  return new Date(Date.now());
}
